import { getSubMassParentTag, tagBuildingFace } from "building-types/buildingGeomTag/buildingGeomTags";
import type { OpeningWrappingMode } from "building-types/openings/openingWrappingModes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { WallAssemblyCladdingLayer, WallAssemblyLayer } from "building-types/walls/wallAssemblyTypes";
import type { WallSection, WallSideDescriptor } from "building-types/walls/wallDesciptorTypes";
import { WallCladdingType } from "building-types/walls/wallDesciptorTypes";
import type {
  HalfEdgeOffsets,
  HalfEdgeTangents,
  WallEndButtJoin,
  WallEndCap,
  WallSideEndJoin,
} from "building-types/walls/wallEndsTypes";
import { WallEndJoinType, ButtCapMode, WallEndSideCornerType } from "building-types/walls/wallEndsTypes";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { StructuralMaterialTable } from "building-types/walls/wallMaterialTypes";
import { WallMaterialType } from "building-types/walls/wallMaterialTypesEnum";
import type { WallSideCrossSection, WallSidePathDomain } from "building-types/walls/wallSideTypes";
import type { Wall, SolidWallMass, SolidWallSide, WallPath, WallMass } from "building-types/walls/wallTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import uid from "common/utils/uid";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { line2EvalOffset, line2Tangent } from "geom/line2";
import { polyLine2ReverseCopy, polyLine2Morph, polyLine2Move } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { vec2Dist, vec2DistSquared, vec2Dot, vec2Negate, vec2Normalize, vec2Sub } from "geom/vec2";
import type { WallDatumAbstractionProvider } from "../datums/datumAbstractionProviders";
import {
  accumWallAssemblyLayerThickness,
  wallAssemblyNew,
  getWallAssemblySubstrateThickness,
  getWallAssemblyStructuralThickness,
  getWallAssemblyFinishThicknessAtWallPathParam,
  getWallAssemblyFinishLayers2,
  getWallAssemblyLayerThickness,
} from "../wallAssemblies/wallAssembly";
import { wallSideFinishMassWrapCrossSection } from "./finishWrap";
import { solidWallMassHalfWall, solidWallMassFromWallEnds } from "./solidWall";
import { solidWallCrossSectionEdges } from "./solidWallCrossSectionUtils";
import { wallPathOpposite } from "./wallDescriptor";
import { wallSectionOpposite } from "./wallDescriptor";
import { wallEndCapNew, halfEdgeTangentsNew, halfEdgeOffsetsNew } from "./wallEnds";
import { wallCrossSectionLayers, wallMassLayers } from "./wallLayers";
import { wallSidePathDomainFromEdge } from "./wallSideBasics";
import { wallSidePathDomainNew } from "./wallSideUtils";
import { isSolidWallMass } from "./wallTypeGuards";

export function wallSidePathDomainReverse({ startT, endT }: WallSidePathDomain): WallSidePathDomain {
  return wallSidePathDomainNew(1 - startT, 1 - endT);
}

export function wallSideAssemblyLayerDomain(
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wallAssemblyLayer: WallAssemblyLayer,
  wallPathLen: number,
): WallSidePathDomain {
  const { startDatumUsage, endDatumUsage } = wallAssemblyLayer;

  const startDatumAbstraction = wallDatumAbstractionProvider.getWallHorizontalDatumAbstraction(
    startDatumUsage,
    "WALL_START",
  );
  const endDatumAbstraction = wallDatumAbstractionProvider.getWallHorizontalDatumAbstraction(endDatumUsage, "WALL_END");

  const sT = startDatumAbstraction.resolveHorizontalDatum(wallPathLen, startDatumUsage.offset) / wallPathLen;
  const eT = endDatumAbstraction.resolveHorizontalDatum(wallPathLen, endDatumUsage.offset) / wallPathLen;

  // TODO - if we need to remap orientation
  // if (isLeftSide) {
  // return wallSidePathDomainReverse(wallSidePathDomainNew(sT, eT))
  // }
  return wallSidePathDomainNew(sT, eT);
}

export function makeWallSideFinishMasses({
  wallDatumAbstractionProvider,
  wallMass,
  wallMaterials,
  isLeftSide,
}: {
  wallDatumAbstractionProvider: WallDatumAbstractionProvider;
  wallMass: WallMass;
  wallMaterials: StructuralMaterialTable;
  isLeftSide: boolean;
}) {
  const finishMasses: SolidWallMass[] = [];
  const finishLayers = wallMassLayers(wallMass, WallLayerType.FINISH, WallLOD.L2);
  const finishCutPlanes = new Set<number>();

  for (const finishLayer of finishLayers) {
    const finishCutPlane = (finishLayer.section.bottomOffset + finishLayer.section.topOffset) / 2;
    finishCutPlanes.add(finishCutPlane);
  }

  for (const finishCutPlane of finishCutPlanes) {
    finishMasses.push(
      ...wallSideCrossSectionFinishMasses(
        wallDatumAbstractionProvider,
        wallMaterials,
        wallMass,
        isLeftSide,
        finishCutPlane,
      ),
    );
  }

  return finishMasses;
}

/**
 * This function extracts horizontal finish splits along a wallPath and subdivides the mass into `WallSideFinishMassses`,
 * which represent a finish mass on one side of the wall, from a startOffset to an endOffset relative to the planarGraph edge.
 */
export function wallSideCrossSectionFinishMasses(
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wallMaterials: StructuralMaterialTable,
  wall: Wall,
  isLeftSide: boolean,
  cutPlane?: number,
) {
  const finishLayerMasses: SolidWallMass[] = [];

  const wallFinishLayers = wallCrossSectionLayers(wall, WallLayerType.FINISH, WallLOD.L2, cutPlane).filter(
    isSolidWallMass,
  );
  if (!wallFinishLayers.length) {
    return finishLayerMasses;
  }

  const addFinishLayerRaw = (wallFinishLayer: SolidWallMass) => {
    /**
     * If there is no substrate layer, this wall can be one of a few things:
     * - a wall finish section that falls beyond the domain of the wall "core"
     * - a wall finish section adjacent to a floor or a foundation
     * we use the inner edge of the finish in this case as the substrate edge.
     */
    const { leftEdge, rightEdge } = wallFinishLayer;
    const substrateEdge = polyLine2ReverseCopy(isLeftSide ? rightEdge : leftEdge);

    finishLayerMasses.push(
      ...wallFinishLayerSideMasses(
        wallDatumAbstractionProvider,
        wallMaterials,
        wallFinishLayer,
        substrateEdge,
        isLeftSide,
      ),
    );
  };

  const wallSubstrateLayers = wallCrossSectionLayers(wall, WallLayerType.SUBSTRATE, WallLOD.L2, cutPlane).filter(
    isSolidWallMass,
  );

  for (const wallFinishLayer of wallFinishLayers) {
    const { startOffset: finishStartOffset, endOffset: finishEndOffset } = wallFinishLayer.section;

    if (!wallSubstrateLayers.length) {
      addFinishLayerRaw(wallFinishLayer);
      continue;
    }

    const wallSubstrateEdges: PolyLine2[] = [];
    wallSubstrateLayers.forEach((wallSubstrateLayer) => {
      const { startOffset, endOffset } = wallSubstrateLayer.section;
      if (endOffset <= finishStartOffset || startOffset >= finishEndOffset) {
        return;
      }

      const substrateEdgeStartHalfWall = solidWallMassHalfWall(wallSubstrateLayer, false);
      const substrateEdgeEndHalfWall = solidWallMassHalfWall(wallSubstrateLayer, true);
      const substrateEnd = isLeftSide ? substrateEdgeEndHalfWall : substrateEdgeStartHalfWall;
      const substrateEdge = substrateEnd.rightEdge;
      wallSubstrateEdges.push(substrateEdge);
    });

    if (!wallSubstrateEdges.length) {
      addFinishLayerRaw(wallFinishLayer);
      continue;
    }

    for (const substrateEdge of wallSubstrateEdges) {
      finishLayerMasses.push(
        ...wallFinishLayerSideMasses(
          wallDatumAbstractionProvider,
          wallMaterials,
          wallFinishLayer,
          substrateEdge,
          isLeftSide,
        ),
      );
    }
  }

  return finishLayerMasses;
}

function wallFinishLayerSideMasses(
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wallMaterials: StructuralMaterialTable,
  wallFinishLayer: SolidWallMass,
  wallSideSubstrateEdge: PolyLine2,
  isLeftSide: boolean,
) {
  const finishLayerMasses: SolidWallMass[] = [];

  const { wallAssembly, section, path } = wallFinishLayer;

  const finishLayers = getWallAssemblyFinishLayers2(wallAssembly, section, isLeftSide);
  if (!finishLayers.length) {
    return finishLayerMasses;
  }

  const wallSidePath = isLeftSide ? wallPathOpposite(path) : path;
  const wallSidePathLen = vec2Dist(wallSidePath.startX, wallSidePath.startY, wallSidePath.endX, wallSidePath.endY);

  const finishEdgeStartHalfWall = solidWallMassHalfWall(wallFinishLayer, false);
  const finishEdgeEndHalfWall = solidWallMassHalfWall(wallFinishLayer, true);

  const finishSideStart = isLeftSide ? finishEdgeEndHalfWall : finishEdgeStartHalfWall;
  const finishSideEnd = isLeftSide ? finishEdgeStartHalfWall : finishEdgeEndHalfWall;

  const wallSideFinishEdge = finishSideStart.rightEdge;
  const wallSideFinishDomain = wallSidePathDomainFromEdge(wallSidePath, wallSideFinishEdge);
  const wallSideSubstrateDomain = wallSidePathDomainFromEdge(wallSidePath, wallSideSubstrateEdge);

  // special case for walls that have been thickened such that the finish no londer overlaps the original
  const wallSideFinishDomainOutsideWall = wallSideFinishDomain.startT >= 1 || wallSideFinishDomain.endT <= 0;
  // special case for walls that have been thickened such that the finish edge is NaN (0 length)
  const wallSideFinishDomainZero = isNaN(wallSideFinishDomain.startT) || isNaN(wallSideFinishDomain.endT);
  // TODO - will need to create a special case for these conditions.
  if (wallSideFinishDomainOutsideWall || wallSideFinishDomainZero) {
    return finishLayerMasses;
  }

  /**
   * Create and sort all unique wall datums - these are relative to the `planarGraph` edge
   * @TODO - implement wallSideHorizontalFinishDatums(finishLayers);
   * @NOTE - Populate this array with values from 0 to 1 for debugging purposes. Eventually will be filled with
   * datums from the finish assembly. 0.5 should produce the same split on either side of the half edge
   * e.g - [-0.5, 0.25,0.5, 0.6, 0.8, 1.5]
   */
  const wallSideHorizontalDatums = [wallSideSubstrateDomain.startT, wallSideSubstrateDomain.endT];

  for (const finishLayer of finishLayers) {
    const { startT, endT } = wallSideAssemblyLayerDomain(wallDatumAbstractionProvider, finishLayer, wallSidePathLen);
    wallSideHorizontalDatums.push(startT);
    wallSideHorizontalDatums.push(endT);
  }

  const wallSectionDatumSet = Array.from(new Set([...wallSideHorizontalDatums]));
  wallSectionDatumSet.sort((a, b) => a - b);

  const finishStartThickness = getWallAssemblyFinishThicknessAtWallPathParam(
    wallDatumAbstractionProvider,
    wallMaterials,
    wallAssembly,
    path,
    wallSideSubstrateDomain.startT + DIST_EPSILON,
    section,
    isLeftSide,
  );

  const leftFinishOffset = finishSideStart.rightOffset - finishStartThickness;

  for (let i = 0; i < wallSectionDatumSet.length - 1; i++) {
    const startOffset = wallSectionDatumSet[i];
    const endOffset = wallSectionDatumSet[i + 1];

    // these datums are outside the bounda of the substrate assembly
    if (startOffset >= wallSideSubstrateDomain.endT || endOffset <= wallSideSubstrateDomain.startT) {
      continue;
    }

    // these datums are outside the bounds of the finish assembly
    if (startOffset >= wallSideFinishDomain.endT || endOffset <= wallSideFinishDomain.startT) {
      continue;
    }

    const finishLayersForDatum = finishLayers.filter((finishLayer) => {
      const { startT, endT } = wallSideAssemblyLayerDomain(wallDatumAbstractionProvider, finishLayer, wallSidePathLen);
      if (endOffset <= startT || endT <= startOffset) {
        return false;
      }
      return true;
    });

    if (!finishLayersForDatum.length) {
      continue;
    }

    const finishThicknessForDatum = accumWallAssemblyLayerThickness(wallMaterials, finishLayersForDatum);
    const finishOffsets = halfEdgeOffsetsNew(-leftFinishOffset, leftFinishOffset + finishThicknessForDatum);

    const startIsWallEnd = startOffset <= wallSideSubstrateDomain.startT + DIST_EPSILON;
    let startCap: WallEndCap;
    if (startIsWallEnd) {
      const wallEndJoin = makeWallSideFinishEndJoin({
        wallMaterials,
        wallFinishLayer,
        wallSideSubstrateEdge,
        wallSideFinishOffsets: finishOffsets,
        isLeftSide,
        isStart: true,
      });
      startCap = makeWallSideFinishSectionCapFromWallEndJoin({
        wallSidePath,
        wallSidePathOffsets: finishOffsets,
        wallSideLeftParam: wallSideSubstrateDomain.startT,
        wallSideRightParam: wallSideFinishDomain.startT,
        wallEndJoin,
        wallEndTangents: finishSideStart,
        isStart: true,
      });
    } else {
      startCap = makeWallSideFinishSectionEndCapFromParam({
        wallSidePath,
        wallSidePathOffsets: finishOffsets,
        wallSidePathParam: startOffset,
        isStart: true,
      });
    }

    const endIsWallEnd = endOffset >= wallSideSubstrateDomain.endT - DIST_EPSILON;
    let endCap: WallEndCap;
    if (endIsWallEnd) {
      const wallEndJoin = makeWallSideFinishEndJoin({
        wallMaterials,
        wallFinishLayer,
        wallSideSubstrateEdge,
        wallSideFinishOffsets: finishOffsets,
        isLeftSide,
        isStart: false,
      });
      endCap = makeWallSideFinishSectionCapFromWallEndJoin({
        wallSidePath,
        wallSidePathOffsets: finishOffsets,
        wallSideLeftParam: wallSideSubstrateDomain.endT,
        wallSideRightParam: wallSideFinishDomain.endT,
        wallEndJoin,
        wallEndTangents: finishSideEnd,
        isStart: false,
      });
    } else {
      endCap = makeWallSideFinishSectionEndCapFromParam({
        wallSidePath,
        wallSidePathOffsets: finishOffsets,
        wallSidePathParam: endOffset,
        isStart: false,
      });
    }

    // TODO WALLS V2 @jaggyluis - hard cast should be removed
    const rightCladdingLayers = finishLayersForDatum as WallAssemblyCladdingLayer[];
    const wallFinishAssembly = wallAssemblyNew({ rightCladdingLayers });
    let wallFinishSection: WallSection = {
      ...section,
      startOffset,
      endOffset,
    };
    if (isLeftSide) {
      wallFinishSection = wallSectionOpposite(wallFinishSection);
    }
    /**
     * This is a side section, oriented to the rightside.
     * The leftSide will always be NONE for now because it's adjacent to a substrate layer
     */
    wallFinishSection = {
      ...wallFinishSection,
      leftSide: {
        ...wallFinishSection.leftSide,
        cladding: WallCladdingType.NONE,
      },
    };

    const mass = solidWallMassFromWallEnds(
      uid(),
      "WALL_LAYER",
      wallFinishLayer.path,
      wallFinishAssembly,
      wallFinishLayer.floorElevation,
      startCap,
      endCap,
      wallFinishSection,
    );

    const subMassTag = getSubMassParentTag(wallFinishLayer.leftFace);
    if (subMassTag) {
      tagBuildingFace(mass.leftFace, subMassTag);
      tagBuildingFace(mass.rightFace, subMassTag);
      if (mass.startFace) {
        tagBuildingFace(mass.startFace, subMassTag);
      }
      if (mass.endFace) {
        tagBuildingFace(mass.endFace, subMassTag);
      }
      tagBuildingFace(mass.topFace, subMassTag);
      tagBuildingFace(mass.bottomFace, subMassTag);
    }

    finishLayerMasses.push(mass);
  }

  return finishLayerMasses;
}

function makeWallSideFinishSectionEndCapFromParam({
  wallSidePath,
  wallSidePathOffsets,
  wallSidePathParam,
  isStart,
}: {
  wallSidePath: WallPath;
  wallSidePathOffsets: HalfEdgeOffsets;
  wallSidePathParam: number;
  isStart: boolean;
}) {
  const { startX, startY, endX, endY } = wallSidePath;
  const [tx, ty] = line2Tangent(startX, startY, endX, endY, !isStart);
  const wallEndTangents = halfEdgeTangentsNew(tx, ty, tx, ty);
  const wallEndJoin: WallEndButtJoin = {
    joinType: WallEndJoinType.BUTT,
    buttCapMode: ButtCapMode.END_ALIGNED,
    buttCapExtension: 0,
    buttCapWrap: 0,
  };
  return makeWallSideFinishSectionCapFromWallEndJoin({
    wallSidePath,
    wallSidePathOffsets,
    wallEndJoin,
    wallEndTangents,
    wallSideLeftParam: wallSidePathParam,
    wallSideRightParam: wallSidePathParam,
    isStart,
  });
}

function makeWallSideFinishSectionCapFromWallEndJoin({
  wallSidePath,
  wallSidePathOffsets,
  wallSideLeftParam,
  wallSideRightParam,
  wallEndJoin,
  wallEndTangents,
  isStart,
}: {
  wallSidePath: WallPath;
  wallSidePathOffsets: HalfEdgeOffsets;
  wallSideLeftParam: number;
  wallSideRightParam: number;
  wallEndJoin: WallSideEndJoin;
  wallEndTangents: HalfEdgeTangents;
  isStart: boolean;
}) {
  switch (wallEndJoin.joinType) {
    case WallEndJoinType.MITER: {
      const leftMiterParam = wallSideLeftParam;
      const rightMiterParam = wallSideRightParam;
      return makeWallSideFinishMiterEnd(
        wallSidePath,
        wallSidePathOffsets,
        leftMiterParam,
        rightMiterParam,
        wallEndTangents,
        isStart,
      );
    }
    case WallEndJoinType.BUTT: {
      const { buttCapExtension, buttCapWrap } = wallEndJoin;
      const buttCapParam = wallSideLeftParam;
      return makeWallSideFinishButtEnd(
        wallSidePath,
        wallSidePathOffsets,
        buttCapExtension,
        buttCapWrap,
        buttCapParam,
        wallEndTangents,
        isStart,
      );
    }
    default: {
      assertUnreachable(wallEndJoin);
    }
  }
}

function makeWallSideFinishButtEnd(
  wallPath: WallPath,
  wallOffsets: HalfEdgeOffsets,
  buttCapExtension: number,
  buttCapWrap: number,
  buttCapParam: number,
  wallEndTangents: HalfEdgeTangents,
  isStart: boolean,
): WallEndCap {
  const { leftOffset, rightOffset } = wallOffsets;
  const { startX, startY, endX, endY } = wallPath;
  const { leftTangentX, leftTangentY, rightTangentX, rightTangentY } = wallEndTangents;
  const [tx, ty] = line2Tangent(startX, startY, endX, endY, !isStart);
  const [lx, ly] = line2EvalOffset(startX, startY, endX, endY, buttCapParam, true, -leftOffset);
  const [rx, ry] = line2EvalOffset(startX, startY, endX, endY, buttCapParam, true, rightOffset);

  let isFreeEnd = true;

  let vertPl = isStart ? polyLine2New([lx, ly, rx, ry]) : polyLine2New([rx, ry, lx, ly]);
  if (buttCapExtension > 0) {
    vertPl = polyLine2Move(vertPl, -tx * buttCapExtension, -ty * buttCapExtension);
  }
  let offsetDifferenceOutline: number[] = [];
  if (buttCapWrap > 0) {
    isFreeEnd = false;
    const leftOffsetAdjusted = -leftOffset - buttCapWrap;
    const [nlx, nly] = line2EvalOffset(startX, startY, endX, endY, buttCapParam, true, leftOffsetAdjusted);
    offsetDifferenceOutline = isStart ? [nlx, nly, rx, ry] : [rx, ry, nlx, nly];
  }

  const verts = [vertPl[0], vertPl[1], vertPl[2], vertPl[3]];

  return wallEndCapNew(
    verts,
    isFreeEnd,
    offsetDifferenceOutline,
    undefined,
    leftOffset,
    rightOffset,
    leftTangentX,
    leftTangentY,
    rightTangentX,
    rightTangentY,
  );
}

function makeWallSideFinishMiterEnd(
  wallPath: WallPath,
  wallOffsets: HalfEdgeOffsets,
  leftMiterParam: number,
  rightMiterParam: number,
  wallEndTangents: HalfEdgeTangents,
  isStart: boolean,
): WallEndCap {
  const { leftOffset, rightOffset } = wallOffsets;
  const { startX, startY, endX, endY } = wallPath;
  const { leftTangentX, leftTangentY, rightTangentX, rightTangentY } = wallEndTangents;
  const [lx, ly] = line2EvalOffset(startX, startY, endX, endY, leftMiterParam, true, -leftOffset);
  const [rx, ry] = line2EvalOffset(startX, startY, endX, endY, rightMiterParam, true, rightOffset);

  const isFreeEnd = false;

  const verts = isStart ? [lx, ly, rx, ry] : [rx, ry, lx, ly];

  return wallEndCapNew(
    verts,
    isFreeEnd,
    undefined,
    undefined,
    leftOffset,
    rightOffset,
    leftTangentX,
    leftTangentY,
    rightTangentX,
    rightTangentY,
  );
}

function makeWallSideFinishEndJoin({
  wallMaterials,
  wallFinishLayer,
  wallSideSubstrateEdge,
  wallSideFinishOffsets,
  isStart,
  isLeftSide,
}: {
  wallMaterials: StructuralMaterialTable;
  wallFinishLayer: SolidWallMass;
  wallSideSubstrateEdge: PolyLine2;
  wallSideFinishOffsets: HalfEdgeOffsets;
  isLeftSide: boolean;
  isStart: boolean;
}): WallSideEndJoin {
  const { path, leftEdge, rightEdge, wallAssembly, section, start, end } = wallFinishLayer;
  const wallSideFinishEdge = isLeftSide ? leftEdge : rightEdge;
  let wallEnd: WallEndCap;
  if (isStart) {
    wallEnd = isLeftSide ? end : start;
  } else {
    wallEnd = isLeftSide ? start : end;
  }

  const { leftOffset, rightOffset } = wallSideFinishOffsets;
  const [pathTangentX, pathTangentY] = line2Tangent(path.startX, path.startY, path.endX, path.endY, isLeftSide);
  const [sideTangentX, sideTangentY] = wallSideCornerTangent(wallFinishLayer, isLeftSide, isStart);
  const [miterTangentX, miterTangentY] = wallEndMiterTangent(wallSideSubstrateEdge, wallSideFinishEdge, isStart);

  const cornerType = wallPathSideCornerType(pathTangentX, pathTangentY, sideTangentX, sideTangentY);
  const joinType = wallPathSideJoinType(
    cornerType,
    leftOffset,
    rightOffset,
    sideTangentX,
    sideTangentY,
    miterTangentX,
    miterTangentY,
  );

  if (joinType === WallEndJoinType.MITER) {
    return { joinType: joinType, shiftTJunction: false };
  }

  let buttCapWrap = 0;
  if (wallEnd.isFreeEnd) {
    const wallOppSideCladding = isLeftSide ? section.rightSide.cladding : section.leftSide.cladding;
    const wallStructuralThickness = getWallAssemblyStructuralThickness(wallMaterials, wallAssembly);
    const wallSideSubstrateThickness = getWallAssemblySubstrateThickness(
      wallMaterials,
      wallAssembly,
      wallFinishLayer.section,
      isLeftSide,
    );

    if (wallOppSideCladding === WallCladdingType.NONE) {
      buttCapWrap += wallStructuralThickness + wallSideSubstrateThickness;
    } else {
      buttCapWrap += wallStructuralThickness / 2 + wallSideSubstrateThickness;
    }
  }

  return { joinType, buttCapMode: ButtCapMode.END_ALIGNED, buttCapExtension: 0, buttCapWrap };
}

/**
 * Calculates the expected default ButtCapMode for intersections between walls
 */
function wallPathSideJoinType(
  cornerType: WallEndSideCornerType,
  sideLeftOffset: number,
  sideRightOffset: number,
  sideTangentX: number,
  sideTangentY: number,
  sideMiterTangentX: number,
  sideMiterTangentY: number,
): WallEndJoinType {
  switch (cornerType) {
    case WallEndSideCornerType.CONCAVE: {
      // concave corners should always be mitered
      return WallEndJoinType.MITER;
    }
    case WallEndSideCornerType.PARALLEL: {
      // parallel corners should always be end aligned
      return WallEndJoinType.BUTT;
    }
    case WallEndSideCornerType.CONVEX: {
      const thickness = sideLeftOffset + sideRightOffset;
      // // we don't want a miter if there is no thickness on this finish section
      if (thickness < DIST_EPSILON) {
        return WallEndJoinType.BUTT;
      }
      const miterDot = vec2Dot(sideTangentX, sideTangentY, sideMiterTangentX, sideMiterTangentY);
      // if the dot product is close to 1, then the adjacent wall has a finish thickness near or at 0.
      if (miterDot < 1 - ANGLE_EPSILON) {
        // 2 thickened corners should be mitered by default
        return WallEndJoinType.MITER;
      } else {
        // 0 thickness intersection creates an end aligned corner
        return WallEndJoinType.BUTT;
      }
    }
    default: {
      assertUnreachable(cornerType);
    }
  }
}

/**
 * Calculates the winding direction of the planar graph tangents on one corner of a miter
 */
function wallPathSideCornerType(
  pathTangentX: number,
  pathTangentY: number,
  pathSideTangentX: number,
  pathSideTangentY: number,
) {
  // we flip the tangents, so that we can deduce the cross section from an approaching vector
  const [sideTangentX, sideTangentY] = vec2Negate(pathSideTangentX, pathSideTangentY);
  // determine the winding direction of the approaching side planar graph edge
  const crossZ = pathTangentX * sideTangentY - pathTangentY * sideTangentX;
  let cornerType: WallEndSideCornerType;
  if (Math.abs(crossZ) < ANGLE_EPSILON) {
    cornerType = WallEndSideCornerType.PARALLEL;
  } else if (crossZ > 0) {
    cornerType = WallEndSideCornerType.CONCAVE;
  } else {
    cornerType = WallEndSideCornerType.CONVEX;
  }
  return cornerType;
}

/**
 *  Extracts the tangents of the adjacent wallMass on a start/end & left/right corner
 */
function wallSideCornerTangent(wallMass: SolidWallMass, isLeftSide: boolean, isStart: boolean): Vec2 {
  const { start, end } = wallMass;
  // sideTangents are the tangents of the adjacent wallMass on a start/end & left/right corner
  let sideTangentX: number;
  let sideTangentY: number;

  if (isLeftSide) {
    if (isStart) {
      sideTangentX = end.rightTangentX;
      sideTangentY = end.rightTangentY;
    } else {
      sideTangentX = start.leftTangentX;
      sideTangentY = start.leftTangentY;
    }
  } else {
    if (isStart) {
      sideTangentX = start.rightTangentX;
      sideTangentY = start.rightTangentY;
    } else {
      sideTangentX = end.leftTangentX;
      sideTangentY = end.leftTangentY;
    }
  }
  return [sideTangentX, sideTangentY];
}

/**
 * Calculates the tangents created by the vector between an outer mass edge and and inner mass edge
 */
function wallEndMiterTangent(innerEdge: PolyLine2, outerEdge: PolyLine2, isStart: boolean): Vec2 {
  const ix = isStart ? 0 : 2;
  const iy = isStart ? 1 : 3;
  let [miterTangentX, miterTangentY] = vec2Sub(innerEdge[ix], innerEdge[iy], outerEdge[ix], outerEdge[iy]);
  [miterTangentX, miterTangentY] = vec2Normalize(miterTangentX, miterTangentY);
  return [miterTangentX, miterTangentY];
}

/**
 * Creates a set of finish layer cross sections of a wall at a cut plane on a single specified side,
 * unlike wallCrossSection which returns the boundary cross section on both sides of the wall.
 *
 * These cross sections are closed polylines representing the footprint of
 * the wall finish layers at that cut on that side, but could be expanded if needed to include more information
 * about the wall, such as offset information, leftEdge/rightEdge/endCaps or information about
 * various miter states.
 *
 * @TODO - consider consolidating the behavior of wallCrossSection with this function
 * ```
 *              |
 *              |
 *    left side | right side
 * +------------|-----------------------------------------+
 * |+-----------|---------------+ +-----++--------------+ |
 * ||+----------|-----------+   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |  A  ||      B       | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * |||          |           |   | |     ||              | |
 * ||+----------|-----------+   | |     ||              | |
 * |+-----------|-----------|---+ +-----|+--------------| |
 * +------------|-----------|---|-------|---------------|-+
 *              |           |   |       |               | |
 *              | STRUCTURE     |       |               | |
 *              | CROSS SECTION |       |               | |
 *              |               |       |               | FINISH
 *              |       SUBSTRATE       |               | CROSS SECTION
 *            EDGE      CROSS SECTION   |               |
 *                                      |               |
 *                             FINISH SIDE              FINISH SIDE
 *                             CROSS SECTION            CROSS SECTION
 *                             FOR FINISH LAYER A       FOR FINISH LAYER B
 * ```
 */

export function wallSideFinishMassLayerCrossSections(
  leftSide: SolidWallSide,
  rightSide: SolidWallSide,
  wallSideFinishMass: SolidWallMass,
  wallMaterials: StructuralMaterialTable,
  isLeftSide: boolean,
  cutPlane?: LevelInches,
) {
  const finishLayerCrossSections: WallSideCrossSection[] = [];

  const { wallAssembly, section, leftEdge, rightEdge, id: finishId, thickness } = wallSideFinishMass;

  if (thickness === 0) {
    return finishLayerCrossSections;
  }

  const finishLayers = getWallAssemblyFinishLayers2(wallAssembly, section, false); // finish side layers are always right

  const innerEdge = leftEdge;
  const outerEdge = polyLine2ReverseCopy(rightEdge);

  let currThickness = 0;
  let currEdge = innerEdge;
  for (const finishLayer of finishLayers) {
    const finishLayerThickness = getWallAssemblyLayerThickness(wallMaterials, finishLayer);

    currThickness += finishLayerThickness;

    const finishEval = currThickness / thickness;
    const finishEdge = polyLine2Morph(outerEdge, innerEdge, finishEval);

    const crossSection: PolyLine2[] = [];

    if (finishLayerThickness > DIST_EPSILON) {
      const wallFinishLayerMass = {
        leftSide,
        rightSide,
        leftEdge: currEdge,
        rightEdge: finishEdge,
        topFace: undefined,
      };
      let includeExteriorCasing: boolean | undefined = undefined;
      if (finishLayer.type !== WallMaterialType.EXTERIOR_FINISH) {
        includeExteriorCasing = false;
      }
      const { leftOutlines, rightOutlines } = solidWallCrossSectionEdges({
        leftSide,
        rightSide,
        wallMass: wallFinishLayerMass,
        cutPlane,
        includeExteriorCasing,
      });

      for (let i = 0; i < leftOutlines.length; i++) {
        const leftFinishEdge = leftOutlines[i];
        const rightFinishEdge = rightOutlines[i];
        if (
          vec2DistSquared(leftFinishEdge[0], leftFinishEdge[1], leftFinishEdge[2], leftFinishEdge[3]) <
          DIST_EPSILON_SQUARED
        ) {
          continue;
        }

        crossSection.push(
          polyLine2New([
            ...polyLine2ReverseCopy(rightFinishEdge),
            ...leftFinishEdge,
            rightFinishEdge[2],
            rightFinishEdge[3],
          ]),
        );
      }

      const openings = [...leftSide.doors, ...leftSide.windows, ...rightSide.doors, ...rightSide.windows]
        .filter((o) => o.opening?.hasWrapping)
        .map((o) => {
          return [o.opening, o.wrapping?.wrappingMode] as [WallOpening, OpeningWrappingMode];
        });
      for (const [opening, wrappingMode] of openings) {
        crossSection.push(
          ...wallSideFinishMassWrapCrossSection({
            opening,
            wrappingMode,
            mass: {
              ...wallFinishLayerMass,
              thickness: currThickness,
            },
            wrapDepth: finishLayerThickness,
            isLeftSide,
          }),
        );
      }
    }

    if (crossSection.length) {
      finishLayerCrossSections.push({
        crossSection,
        layer: finishLayer,
        thickness: finishLayerThickness,
        finishId,
      });
    }

    currEdge = finishEdge;
  }

  return finishLayerCrossSections;
}

export function wallSideIsExterior(side?: WallSideDescriptor): boolean {
  const enclosure = side?.wallRoom?.roomEnclosure;
  if (enclosure === undefined) {
    return false;
  }
  return !isInteriorRoomEnclosure[enclosure];
}
